import {
  generateCodeVerifier,
  generateCodeChallenge,
  storeCodeVerifier,
  getCodeVerifier,
  clearCodeVerifier
} from '../utils/pkce'
import { storage } from '../utils/storage'
import { api } from './api'
import type { User, AuthCallbackResponse } from '../types/auth'

const DISCORD_CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID
const DISCORD_AUTHORIZE_URL = 'https://discord.com/api/oauth2/authorize'

/**
 * Initiate Discord OAuth login flow with PKCE
 * Redirects the user to Discord's authorization page
 */
export async function initiateDiscordLogin(): Promise<void> {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  // Store the code verifier for later use in the callback
  storeCodeVerifier(codeVerifier)

  const params = new URLSearchParams({
    client_id: DISCORD_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  window.location.href = `${DISCORD_AUTHORIZE_URL}?${params.toString()}&scope=identify+guilds.join`
}

/**
 * Handle the OAuth callback after Discord redirects back
 * Exchanges the authorization code for a session token
 * @param code The authorization code from Discord
 * @returns The authenticated user data
 */
export async function handleOAuthCallback(code: string): Promise<User> {
  const codeVerifier = getCodeVerifier()

  if (!codeVerifier) {
    throw new Error('No code verifier found. Please restart the login process.')
  }

  try {
    // Exchange the code for a session token via our backend
    const response = await api.post<AuthCallbackResponse>('/api/auth/callback', {
      code,
      code_verifier: codeVerifier,
    })

    // Store the session token and user data
    storage.setToken(response.token)
    storage.setUser(response.user)

    // Clean up the code verifier
    clearCodeVerifier()

    return response.user
  } catch (error) {
    clearCodeVerifier()
    throw error
  }
}

/**
 * Validate the current session token
 * @returns The current user or null if session is invalid
 */
export async function validateSession(): Promise<User | null> {
  const token = storage.getToken()

  if (!token) {
    return null
  }

  try {
    const response = await api.get<{ user: User }>('/api/session')
    storage.setUser(response.user)
    return response.user
  } catch (error) {
    // Session is invalid, clear stored data
    storage.clear()
    return null
  }
}

/**
 * Logout the current user
 * Clears local storage and optionally calls the backend logout endpoint
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/api/auth/logout')
  } catch (error) {
    console.error('Logout error:', error)
  } finally {
    storage.clear()
    clearCodeVerifier()
  }
}

/**
 * Get the currently stored user from local storage
 * @returns The user data or null
 */
export function getCurrentUser(): User | null {
  return storage.getUser()
}
